/**
 * Payroll Admin Dashboard
 *
 * Admin view to search and display employee payroll information from PainelGerencial.
 * Uses tRPC to fetch data from the payroll router.
 *
 * Features:
 * - Employee search with autocomplete
 * - Current payroll summary with breakdown
 * - Payment history
 * - Provision history (for CLT employees)
 */
import { useState, useEffect, useRef } from 'react'
import { trpc } from '@/api/trpc'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PayrollLookupSkeleton } from '@/components/common/PayrollLookupSkeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { usePageTitle } from '@/stores/page-title-store'
import {
  DollarSign,
  Calendar,
  TrendingUp,
  Briefcase,
  FileText,
  AlertCircle,
  Car,
  Utensils,
  Heart,
  Home,
  Phone,
  Gift,
  Minus,
  Search,
  User,
  Users,
  Loader2,
  X,
} from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface EmployeeResult {
  id: number
  funcionario: string
  email: string | null
  contrato: string | null
  situacao: string | null
  setor: number | null
}

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount)
}

function formatMesFolha(mesFolha: string): string {
  const month = parseInt(mesFolha.substring(0, 2))
  const year = mesFolha.substring(2)
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]
  return `${monthNames[month - 1]} ${year}`
}

function formatMesAno(mesano: string): string {
  if (mesano.includes('-')) {
    const [year, month] = mesano.split('-')
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ]
    return `${monthNames[parseInt(month) - 1]}/${year}`
  }
  return formatMesFolha(mesano)
}

// Convert mesFolha (MMYYYY) to sortable number (YYYYMM)
function mesFolhaToSortable(mesFolha: string): number {
  const month = mesFolha.substring(0, 2)
  const year = mesFolha.substring(2)
  return parseInt(year + month)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()
}

// ============================================================================
// Components
// ============================================================================

function PayrollSummaryCard({
  title,
  value,
  icon: Icon,
  variant = 'default',
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  variant?: 'default' | 'success' | 'warning' | 'error'
}) {
  const variantClasses = {
    default: 'text-brand-gray',
    success: 'text-green-400',
    warning: 'text-yellow-400',
    error: 'text-red-400',
  }

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-brand-gray">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${variantClasses[variant]}`}>
            {typeof value === 'number' ? formatCurrency(value) : value}
          </p>
        </div>
        <div className="p-2 bg-brand-dark-lighter rounded-lg">
          <Icon className={`w-5 h-5 ${variantClasses[variant]}`} />
        </div>
      </div>
    </Card>
  )
}

function BreakdownItem({
  label,
  value,
  icon: Icon,
  isNegative = false,
}: {
  label: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  isNegative?: boolean
}) {
  if (value === 0) return null

  return (
    <div className="flex items-center justify-between py-2 border-b border-brand-dark-lighter last:border-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-brand-gray" />
        <span className="text-sm text-white">{label}</span>
      </div>
      <span className={`text-sm font-medium ${isNegative ? 'text-red-400' : 'text-green-400'}`}>
        {isNegative ? '- ' : ''}{formatCurrency(Math.abs(value))}
      </span>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function PayrollLookupPage() {
  usePageTitle('Folha de Pagamento')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeResult | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Search employees
  const { data: searchResults, isLoading: searchLoading } = trpc.payroll.searchEmployees.useQuery(
    { query: debouncedQuery, limit: 10 },
    { enabled: debouncedQuery.length >= 2 }
  )

  // Fetch payroll data for selected employee
  const selectedEmail = selectedEmployee?.email || ''

  const { data: summary, isLoading: summaryLoading, error: summaryError } = trpc.payroll.getByEmail.useQuery(
    { email: selectedEmail },
    { enabled: !!selectedEmail }
  )

  const {
    data: history,
    isLoading: historyLoading,
  } = trpc.payroll.getHistory.useQuery(
    { email: selectedEmail, limit: 12 },
    { enabled: !!selectedEmail }
  )

  const {
    data: provisions,
    isLoading: provisionsLoading,
  } = trpc.payroll.getProvision.useQuery(
    { email: selectedEmail, limit: 12 },
    { enabled: !!selectedEmail }
  )

  const isLoading = summaryLoading || historyLoading || provisionsLoading

  const handleSelectEmployee = (employee: EmployeeResult) => {
    setSelectedEmployee(employee)
    setSearchQuery(employee.funcionario)
    setIsDropdownOpen(false)
  }

  const handleClear = () => {
    setSearchQuery('')
    setDebouncedQuery('')
    setSelectedEmployee(null)
    inputRef.current?.focus()
  }

  const handleInputFocus = () => {
    if (searchQuery.length >= 2) {
      setIsDropdownOpen(true)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)
    setIsDropdownOpen(value.length >= 2)
    // Clear selection if user types after selecting
    if (selectedEmployee && value !== selectedEmployee.funcionario) {
      setSelectedEmployee(null)
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Search with Autocomplete */}
      <Card className="p-6 relative z-10">
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gray pointer-events-none z-10" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Buscar colaborador por nome ou email..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              className="pl-10 pr-10"
            />
            {(searchQuery || selectedEmployee) && (
              <button
                onClick={handleClear}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute z-50 w-full mt-2 bg-brand-dark-light border border-gray-700 rounded-lg shadow-xl max-h-80 overflow-auto">
              {searchLoading ? (
                <div className="p-4 flex items-center justify-center gap-2 text-brand-gray">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Buscando...</span>
                </div>
              ) : searchResults?.employees && searchResults.employees.length > 0 ? (
                <div className="py-2">
                  {searchResults.employees.map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => handleSelectEmployee(emp)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-brand-dark-lighter transition-colors text-left"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center text-brand-dark text-sm font-bold">
                        {getInitials(emp.funcionario)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">
                          {emp.funcionario}
                        </div>
                        <div className="text-brand-gray text-sm truncate">
                          {emp.email || 'Sem email'}
                        </div>
                      </div>
                      <Badge className="bg-brand-primary/20 text-brand-primary text-xs">
                        {emp.contrato || 'N/A'}
                      </Badge>
                    </button>
                  ))}
                </div>
              ) : debouncedQuery.length >= 2 ? (
                <div className="p-4 text-center text-brand-gray">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum colaborador encontrado</p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Selected employee chip */}
        {selectedEmployee && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-brand-gray">Selecionado:</span>
            <div className="inline-flex items-center gap-2 bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-full">
              <User className="w-4 h-4" />
              <span className="font-medium">{selectedEmployee.funcionario}</span>
              <button onClick={handleClear} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* No selection state */}
      {!selectedEmployee && (
        <Card className="p-12">
          <EmptyState
            icon={User}
            title="Selecione um colaborador"
            description="Digite o nome ou email de um colaborador acima para visualizar sua folha de pagamento."
          />
        </Card>
      )}

      {/* Loading state */}
      {selectedEmployee && isLoading && (
        <PayrollLookupSkeleton />
      )}

      {/* Error state */}
      {selectedEmployee && !isLoading && summaryError && (
        <Card className="p-6">
          <EmptyState
            icon={AlertCircle}
            title="Erro ao carregar dados"
            description={`Não foi possível carregar os dados de folha de pagamento para: ${selectedEmployee.funcionario}`}
          />
        </Card>
      )}

      {/* Success state - show payroll data */}
      {selectedEmployee && !isLoading && summary && (
        <>
          {/* Employee Info */}
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-brand-dark" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {summary.employee.funcionario}
                </h2>
                <div className="flex items-center gap-2 text-brand-gray">
                  <span>{summary.employee.email}</span>
                  <Badge className="bg-brand-primary/20 text-brand-primary">
                    {summary.employee.contrato || 'N/A'}
                  </Badge>
                  <Badge className={summary.employee.situacao === 'A' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                    {summary.employee.situacao === 'A' ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PayrollSummaryCard
              title="Total Líquido"
              value={summary.calculatedTotal}
              icon={DollarSign}
              variant="success"
            />
            <PayrollSummaryCard
              title="Salário Base"
              value={summary.breakdown.baseSalary}
              icon={Briefcase}
            />
            <PayrollSummaryCard
              title="Benefícios"
              value={summary.breakdown.benefits}
              icon={Gift}
            />
            <PayrollSummaryCard
              title="Descontos"
              value={summary.breakdown.deductions}
              icon={Minus}
              variant="error"
            />
          </div>

          {/* Current Payroll Breakdown */}
          {summary.currentPayroll && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-brand-primary" />
                Pagamento Atual - {formatMesFolha(summary.currentPayroll.mesFolha)}
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Earnings */}
                <div>
                  <h3 className="text-sm font-medium text-brand-gray mb-3">Proventos</h3>
                  <div className="bg-brand-dark-lighter rounded-lg p-4">
                    <BreakdownItem label="Salário" value={summary.currentPayroll.Salario} icon={DollarSign} />
                    <BreakdownItem label="Combustível" value={summary.currentPayroll.Combustivel} icon={Car} />
                    <BreakdownItem label="Alimentação" value={summary.currentPayroll.Alimentacao} icon={Utensils} />
                    <BreakdownItem label="Plano de Saúde" value={summary.currentPayroll.PlanoSaude} icon={Heart} />
                    <BreakdownItem label="Aux. Moradia" value={summary.currentPayroll.AuxMoradia} icon={Home} />
                    <BreakdownItem label="Aux. Telefone" value={summary.currentPayroll.AuxTel} icon={Phone} />
                    <BreakdownItem label="13º Salário" value={summary.breakdown.thirteenthSalary} icon={Gift} />
                    <BreakdownItem label="Extras" value={summary.currentPayroll.Extras} icon={TrendingUp} />
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="text-sm font-medium text-brand-gray mb-3">Descontos</h3>
                  <div className="bg-brand-dark-lighter rounded-lg p-4">
                    <BreakdownItem label="Adiantamento" value={Math.abs(summary.currentPayroll.d_Adiantamento)} icon={Minus} isNegative />
                    <BreakdownItem label="Garagem" value={Math.abs(summary.currentPayroll.d_Garagem)} icon={Minus} isNegative />
                    <BreakdownItem label="Inglês" value={Math.abs(summary.currentPayroll.d_Ingles)} icon={Minus} isNegative />
                    <BreakdownItem label="Plano" value={Math.abs(summary.currentPayroll.d_Plano)} icon={Minus} isNegative />
                    <BreakdownItem label="Academia" value={Math.abs(summary.currentPayroll.d_Academia)} icon={Minus} isNegative />
                    <BreakdownItem label="Encargos" value={Math.abs(summary.currentPayroll.d_Encargos)} icon={Minus} isNegative />
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="mt-6 pt-4 border-t border-brand-dark-lighter flex justify-between items-center">
                <span className="text-lg font-medium text-white">Total Líquido</span>
                <span className="text-2xl font-bold text-brand-primary">
                  {formatCurrency(summary.currentPayroll.total)}
                </span>
              </div>
            </Card>
          )}

          {/* No current payroll */}
          {!summary.currentPayroll && (
            <Card className="p-6">
              <EmptyState
                icon={FileText}
                title="Sem pagamento atual"
                description="Este colaborador não possui registros de pagamento PJ."
              />
            </Card>
          )}

          {/* Tabs for History and Provisions */}
          <Tabs defaultValue="history" className="space-y-4">
            <TabsList>
              <TabsTrigger value="history">Histórico de Pagamentos</TabsTrigger>
              <TabsTrigger value="provisions">Provisões</TabsTrigger>
            </TabsList>

            {/* Payment History */}
            <TabsContent value="history">
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Histórico de Pagamentos
                  {history && (
                    <span className="text-sm font-normal text-brand-gray ml-2">
                      ({history.totalRecords} registros)
                    </span>
                  )}
                </h2>

                {!history?.payments?.length ? (
                  <EmptyState
                    icon={FileText}
                    title="Sem histórico"
                    description="Nenhum pagamento anterior encontrado."
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-brand-dark-lighter">
                          <th className="text-left py-3 px-2 text-brand-gray font-medium">Mês</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Salário</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Benefícios</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Descontos</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...history.payments]
                          .sort((a, b) => mesFolhaToSortable(b.mesFolha) - mesFolhaToSortable(a.mesFolha))
                          .map((payment) => {
                          const benefits =
                            payment.Combustivel +
                            payment.Alimentacao +
                            payment.PlanoSaude +
                            payment.AuxMoradia +
                            payment.AuxTel
                          const deductions = Math.abs(
                            payment.d_Adiantamento +
                            payment.d_Garagem +
                            payment.d_Ingles +
                            payment.d_Plano +
                            payment.d_Academia +
                            payment.d_Encargos
                          )

                          return (
                            <tr key={payment.recno} className="border-b border-brand-dark-lighter hover:bg-brand-dark-lighter/50">
                              <td className="py-3 px-2 text-white">{formatMesFolha(payment.mesFolha)}</td>
                              <td className="py-3 px-2 text-right text-white">{formatCurrency(payment.Salario)}</td>
                              <td className="py-3 px-2 text-right text-green-400">{formatCurrency(benefits)}</td>
                              <td className="py-3 px-2 text-right text-red-400">-{formatCurrency(deductions)}</td>
                              <td className="py-3 px-2 text-right font-medium text-brand-primary">{formatCurrency(payment.total)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Provisions (CLT) */}
            <TabsContent value="provisions">
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-white mb-4">
                  Provisões Mensais
                  {provisions && (
                    <span className="text-sm font-normal text-brand-gray ml-2">
                      ({provisions.totalRecords} registros)
                    </span>
                  )}
                </h2>

                {!provisions?.provisions?.length ? (
                  <EmptyState
                    icon={FileText}
                    title="Sem provisões"
                    description="Nenhuma provisão encontrada. Provisões são calculadas apenas para contratos CLT."
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-brand-dark-lighter">
                          <th className="text-left py-3 px-2 text-brand-gray font-medium">Mês</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Salário</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">FGTS</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">INSS</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Custo Real</th>
                          <th className="text-right py-3 px-2 text-brand-gray font-medium">Provisão</th>
                        </tr>
                      </thead>
                      <tbody>
                        {provisions.provisions.map((prov) => (
                          <tr key={prov.recno} className="border-b border-brand-dark-lighter hover:bg-brand-dark-lighter/50">
                            <td className="py-3 px-2 text-white">{formatMesAno(prov.mesano)}</td>
                            <td className="py-3 px-2 text-right text-white">{formatCurrency(prov.salario)}</td>
                            <td className="py-3 px-2 text-right text-white">{formatCurrency(prov.fgts)}</td>
                            <td className="py-3 px-2 text-right text-white">{formatCurrency(prov.inssemp)}</td>
                            <td className="py-3 px-2 text-right text-blue-400">{formatCurrency(prov.crel)}</td>
                            <td className="py-3 px-2 text-right font-medium text-brand-primary">{formatCurrency(prov.cprov)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  )
}

export default PayrollLookupPage
